import React, { useState } from 'react';
import {
  LuMapPin,
  LuPhone,
  LuMail,
  LuClock,
  LuSend,
  LuCircleCheck,
  LuBookOpen,
  LuSchool,
  LuUsers,
  LuLoader
} from 'react-icons/lu';
import { PiChurchLight, PiHandsPraying } from 'react-icons/pi';
import contactHero from '../../assets/Contactus.png';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../../shared/firebase';

export default function ContactUs({ activeSection = 'contact', onSelectSection = () => { } }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: 'General Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Calculate order index
      let currentOrder = 1;
      try {
        let snap = await getDocs(collection(db, 'contact-us')).catch(() => null);
        if (!snap || snap.empty) {
          snap = await getDocs(collection(db, 'contact_us')).catch(() => null);
        }
        if (snap) {
          currentOrder = snap.size + 1;
        }
      } catch (e) {
        currentOrder = Date.now();
      }

      // Comprehensive payload: saves EVERY field in both PascalCase and lowercase formats
      const messageDoc = {
        Name: formData.name.trim(),
        name: formData.name.trim(),
        Email: formData.email.trim(),
        email: formData.email.trim(),
        Phone: formData.phone.trim() || '',
        phone: formData.phone.trim() || '',
        Category: formData.category || 'General Inquiry',
        category: formData.category || 'General Inquiry',
        Message: formData.message.trim(),
        message: formData.message.trim(),
        order: currentOrder,
        time_stamp: new Date(),
        createdAt: serverTimestamp()
      };

      // Save primarily to Firestore 'contact-us' collection (with fallbacks)
      try {
        await addDoc(collection(db, 'contact-us'), messageDoc);
      } catch (err) {
        try {
          await addDoc(collection(db, 'contact_us'), messageDoc);
        } catch (e2) {
          await addDoc(collection(db, 'contacts'), messageDoc);
        }
      }

      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', category: 'General Inquiry', message: '' });
      setTimeout(() => setIsSuccess(false), 8000);
    } catch (error) {
      console.error("Error submitting contact message to Firebase:", error);
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', category: 'General Inquiry', message: '' });
      setTimeout(() => setIsSuccess(false), 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2D2420] font-sans pb-16">

      {/* ── 1. Hero Header Banner ── */}
      <section className="relative w-full overflow-hidden min-h-[380px] sm:min-h-[420px] lg:min-h-[440px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={contactHero}
            alt="Contact CSI Diocese of Malabar"
            className="w-full h-full object-cover object-[right_center] filter brightness-[0.75] contrast-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#140e1b]/90 via-[#140e1b]/40 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-16 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          
          <div className="max-w-xl text-left">
            <h1 className="font-serif font-bold text-white text-[38px] sm:text-[48px] lg:text-[56px] tracking-[0.04em] uppercase leading-[1.05] drop-shadow-md">
              CONTACT US
            </h1>
            <p className="text-[#E8B86D] text-[15px] sm:text-[17px] font-medium tracking-wide mt-2.5 drop-shadow-sm">
              We welcome your prayers, inquiries, and fellowship. Reach out to the Diocesan Secretariat, Bishop's Office, or parish leadership.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium mt-4">
              <span onClick={() => onSelectSection('home')} className="hover:text-white cursor-pointer transition-colors">Home</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span className="text-white font-semibold">Contact Us</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuMapPin className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">Kozhikode</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Diocesan Centre</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuClock className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">9 AM - 5 PM</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Working Hours</span>
              </div>
            </div>
          </div>

        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-12 text-[#FAF7F2] fill-current">
            <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* ── 2. Main Contact Grid ── */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-10">

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

          {/* Left: Contact Info Directory (Col 5) */}
          <div className="lg:col-span-5 bg-white rounded-[28px] p-6 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100 space-y-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#842343] block mb-1">Official Secretariat</span>
              <h2 className="font-serif font-bold text-[#1a1715] text-[22px] sm:text-[24px]">
                CSI Diocesan Headquarters
              </h2>
              <p className="text-xs text-[#8A7E79] mt-1">
                Diocesan Office, Bishop's House &amp; Administrative Secretariat.
              </p>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-[#FAF7F2] border border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-[#842343] text-white flex items-center justify-center shrink-0 shadow-xs">
                <LuMapPin className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-[#8A7E79] uppercase tracking-wider block mb-0.5">Postal Address</span>
                <p className="font-serif font-bold text-[#1a1715] text-[14px] leading-snug">
                  CSI Diocesan Office, Bishop's House
                </p>
                <p className="text-[12px] text-[#5A4F4A] mt-0.5">
                  Bank Road, Kozhikode - 673001, Kerala, India
                </p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-[#FAF7F2] border border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-[#842343] text-white flex items-center justify-center shrink-0 shadow-xs">
                <LuPhone className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-[#8A7E79] uppercase tracking-wider block mb-0.5">Telephone Numbers</span>
                <div className="flex flex-wrap gap-2 text-[13.5px] font-bold text-[#1a1715]">
                  <a href="tel:04952721748" className="hover:text-[#842343] transition-colors">0495 2721748</a>
                  <span>/</span>
                  <a href="tel:04952721084" className="hover:text-[#842343] transition-colors">0495 2721084</a>
                </div>
                <p className="text-[11px] text-[#8A7E79] mt-0.5">
                  Monday to Saturday: 9:00 AM – 5:00 PM
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-[#FAF7F2] border border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-[#842343] text-white flex items-center justify-center shrink-0 shadow-xs">
                <LuMail className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-[#8A7E79] uppercase tracking-wider block mb-0.5">Official Email</span>
                <a href="mailto:csimalabardiocese@gmail.com" className="font-sans font-bold text-[#842343] text-[13.5px] hover:underline block">
                  csimalabardiocese@gmail.com
                </a>
                <p className="text-[11px] text-[#8A7E79] mt-0.5">
                  bishop@csimalabar.org
                </p>
              </div>
            </div>

            {/* Prayer Line Banner */}
            <div className="bg-[#faf5ef] rounded-2xl p-4 border border-[#f2e7db] flex items-center gap-3">
              <PiHandsPraying className="w-7 h-7 text-[#842343] shrink-0" />
              <div className="text-xs text-[#5A4F4A]">
                <strong className="text-[#1a1715] block font-serif">Pastoral Prayer Requests</strong>
                Submit your prayer concerns directly to the Bishop's prayer circle.
              </div>
            </div>
          </div>

          {/* Right: Interactive Message Form (Col 7) */}
          <div className="lg:col-span-7 bg-white rounded-[28px] p-6 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#842343] block mb-1">Get in Touch</span>
              <h2 className="font-serif font-bold text-[#1a1715] text-[24px] sm:text-[26px] mb-2">
                Send Us a Message
              </h2>
              <p className="text-xs text-[#8A7E79] mb-6">
                Fill in the form below and our administrative desk will respond promptly.
              </p>
            </div>

            {isSuccess && (
              <div className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-center gap-3 text-xs font-medium">
                <LuCircleCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                <span>Your message has been sent successfully to the Diocesan Secretariat. God bless you!</span>
              </div>
            )}

            {errorMessage && (
              <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-800 text-xs font-medium">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Full Name */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5A4F4A] mb-1.5">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="w-full bg-[#fdfaf7] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-[#1a1715] outline-none focus:border-[#842343] transition-colors"
                  />
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5A4F4A] mb-1.5">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@csi.com"
                    className="w-full bg-[#fdfaf7] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-[#1a1715] outline-none focus:border-[#842343] transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Phone */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5A4F4A] mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="w-full bg-[#fdfaf7] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-[#1a1715] outline-none focus:border-[#842343] transition-colors"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5A4F4A] mb-1.5">
                    Inquiry Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full bg-[#fdfaf7] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-[#5A4F4A] outline-none focus:border-[#842343] transition-colors cursor-pointer"
                  >
                    <option>General Inquiry</option>
                    <option>Prayer Request</option>
                    <option>Parish Matters</option>
                    <option>Institutions &amp; Schools</option>
                    <option>Bishop's Office Appointment</option>
                    <option>Video Wishing Message Request</option>
                    <option>Donation &amp; Support</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5A4F4A] mb-1.5">
                  Your Message or Prayer Request *
                </label>
                <textarea
                  name="message"
                  required
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Type your message, prayer concern, or feedback here..."
                  className="w-full bg-[#fdfaf7] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-[#1a1715] outline-none focus:border-[#842343] transition-colors resize-none"
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#842343] hover:bg-[#6c1b35] text-white font-sans font-semibold text-[13px] py-3.5 px-6 rounded-xl shadow-xs hover:shadow-md transition-all flex items-center justify-center gap-2 active:scale-98 cursor-pointer disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <LuLoader className="w-4 h-4 animate-spin" />
                    <span>Sending Message...</span>
                  </>
                ) : (
                  <>
                    <LuSend className="w-4 h-4" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </div>

        </div>

        {/* ── 3. Google Maps Location Banner ── */}
        <div
          onClick={() => window.open('https://maps.google.com/?q=CSI+Diocesan+Office+Bank+Road+Kozhikode', '_blank')}
          className="bg-white rounded-[28px] p-6 sm:p-8 shadow-[0_6px_25px_rgba(0,0,0,0.03)] border border-gray-100 cursor-pointer group hover:border-[#842343]/50 transition-all"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#842343] block mb-1">Find Our Headquarters</span>
              <h3 className="font-serif font-bold text-[#1a1715] text-[20px]">
                Diocesan Secretariat on Google Maps
              </h3>
            </div>
            <span className="text-[#842343] font-bold text-xs flex items-center gap-1 group-hover:translate-x-1 transition-transform">
              Open in Google Maps ↗
            </span>
          </div>

          <div className="w-full h-48 bg-[#e5ecdb] rounded-2xl relative overflow-hidden flex items-center justify-center border border-gray-200">
            <div className="absolute inset-0 opacity-60 bg-[radial-gradient(#b8ccaa_2px,transparent_2px)] [background-size:16px_16px]"></div>
            <div className="relative z-10 bg-white/95 backdrop-blur-xs px-5 py-3 rounded-2xl shadow-lg border border-white flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#842343] text-white flex items-center justify-center shadow-xs">
                <LuMapPin className="w-4 h-4" />
              </div>
              <div className="text-left">
                <strong className="text-[13px] text-[#1a1715] font-serif block">CSI Diocese of Malabar</strong>
                <span className="text-[11px] text-[#8A7E79]">Bank Road, Kozhikode - 673001</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── 4. Bottom Statistics Strip ── */}
      <section className="mt-14 pt-8 pb-8 bg-[#f5ede5]/70 border-t border-[#ede2d6]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 text-center">

            <div className="flex items-center justify-center gap-3">
              <PiChurchLight className="w-7 h-7 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">26+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Dioceses</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuBookOpen className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">5</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Districts</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuSchool className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">120+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Institutions</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">49</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Presbyters</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">4.5M+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Members</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <span className="text-[#842343] text-xl font-serif">✝</span>
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">100+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Worship Services</span>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
